import { useMemo, useCallback } from 'react';

import { scaleLinear } from 'd3-scale';
import _flatten from 'lodash/flatten';
import { isNumber } from 'lodash';
import { BuoyTooltipData } from 'types/buoys';
import { Swell } from 'types/swell';

import type {
  SwellSpectraDataHours,
  SwellSpectraWaveDataPoint,
  SwellSpectraData,
} from 'types/spectra';
import type { SwellsTooltipData } from 'types/graphs';

import { SWELL_SPECTRA_GRAPH_HEIGHT } from 'common/constants';

const SWELL_SPECTRA_GRAPH_X_PADDING = 60;
const SWELL_SPECTRA_GRAPH_X_PADDING_MOBILE = 40;
const SWELL_SPECTRA_GRAPH_Y_PADDING = 10;

const getTop3SwellsByPower = (swells: Array<Swell>) =>
  [...swells]
    .sort((a, b) => {
      if (isNumber(b?.power) && isNumber(a?.power)) {
        return b.power - a.power;
      }
      return 0;
    })
    .slice(0, 3);

interface Props {
  activeWave: SwellsTooltipData | BuoyTooltipData | null;
  dayIndex: number;
  daysToDisplay: Array<number>;
  fixedScale: boolean;
  graphWidth: number;
  overallMaxPeriod: number;
  period: Array<number> | null;
  hourIndex: number;
  interval: 1 | 2;
  isDesktop: boolean;
  swellSpectraData: SwellSpectraData;
  isBuoySwellSpectraGraph?: boolean;
  currentBuoyTimestamp?: number;
}

const useSwellSpectra = ({
  activeWave,
  dayIndex,
  daysToDisplay,
  fixedScale,
  graphWidth,
  overallMaxPeriod,
  period,
  hourIndex,
  interval,
  isDesktop,
  swellSpectraData,
  isBuoySwellSpectraGraph,
  currentBuoyTimestamp,
}: Props) => {
  const days = useMemo(() => {
    const daysArray = swellSpectraData?.days?.filter(
      (day: SwellSpectraDataHours, index: number) =>
        index >= daysToDisplay[0] && index <= daysToDisplay[daysToDisplay.length - 1],
    );

    return !swellSpectraData?.loading ? daysArray : null;
  }, [swellSpectraData?.days, swellSpectraData?.loading, daysToDisplay]);

  const hours: SwellSpectraDataHours = useMemo(() => _flatten(days), [days]);

  const sortedTop3Swells = useMemo(() => {
    if (!activeWave?.data?.swells?.length) return [];

    return isDesktop
      ? getTop3SwellsByPower(activeWave.data.swells)
      : getTop3SwellsByPower(activeWave.data.swells).slice(0, 1);
  }, [activeWave?.data?.swells, isDesktop]);

  const rawPeriodRange = useMemo(() => {
    const max = overallMaxPeriod > 10 ? overallMaxPeriod : 10;
    const range = swellSpectraData?.period?.slice(0, max) || [];
    return range;
  }, [overallMaxPeriod, swellSpectraData?.period]);

  const rawBuoyPeriodRange = useMemo(
    () => activeWave?.data?.swellSpectra?.period,
    [activeWave?.data],
  );

  const periodRange = useMemo(() => {
    if (isBuoySwellSpectraGraph) {
      // If we are in the buoys graph and the filtered pre processed array is long enough, we should use that.
      // Otherwise we will use an array with a range of 2 - 10 to have a minimum of 9 data points displayed.
      let range = [2, 3, 4, 5, 6, 7, 8, 9, 10];

      if (period?.length) {
        range = period?.length > 10 ? period : range;
      }

      if (interval === 1) {
        return range;
      }

      return range.filter((p) => p % interval === 0);
    }

    // We want to ensure if the max swell period is less than 10 we bottom it out at 10 so it can not be lower.
    const max = overallMaxPeriod > 10 ? overallMaxPeriod : 10;
    const range = swellSpectraData?.period?.slice(0, max) || [];
    if (interval === 1) {
      return range;
    }
    return range.filter((p) => p % interval === 0);
  }, [period, interval, isBuoySwellSpectraGraph, overallMaxPeriod, swellSpectraData?.period]);

  const currentSpread = useMemo(() => {
    const max = overallMaxPeriod > 10 ? overallMaxPeriod : 10;

    if (interval === 1) {
      return days?.[dayIndex]?.[hourIndex]?.spread?.slice(0, max) || [];
    }

    return (
      days?.[dayIndex]?.[hourIndex]?.spread?.slice(0, max)?.filter((s, i) => i % interval === 0) ||
      []
    );
  }, [dayIndex, days, hourIndex, interval, overallMaxPeriod]);

  const currentDirection = useMemo(() => {
    const max = overallMaxPeriod > 10 ? overallMaxPeriod : 10;

    if (interval === 1) {
      return days?.[dayIndex]?.[hourIndex]?.direction?.slice(0, max) || [];
    }

    return (
      days?.[dayIndex]?.[hourIndex]?.direction
        ?.slice(0, max)
        ?.filter((d, i) => i % interval === 0) || []
    );
  }, [dayIndex, days, hourIndex, interval, overallMaxPeriod]);

  const currentEnergy = useMemo(() => {
    const max = overallMaxPeriod > 10 ? overallMaxPeriod : 10;

    if (interval === 1) {
      return days?.[dayIndex]?.[hourIndex]?.energy?.slice(0, max) || [];
    }

    return (
      days?.[dayIndex]?.[hourIndex]?.energy?.slice(0, max)?.filter((e, i) => i % interval === 0) ||
      []
    );
  }, [dayIndex, days, hourIndex, interval, overallMaxPeriod]);

  const currentRawEnergy = useMemo(() => {
    if (isBuoySwellSpectraGraph) {
      return activeWave?.data?.swellSpectra?.energy ?? null;
    }

    const max = overallMaxPeriod > 10 ? overallMaxPeriod : 10;
    return days?.[dayIndex]?.[hourIndex]?.energy?.slice(0, max) || [];
  }, [activeWave?.data, dayIndex, days, hourIndex, isBuoySwellSpectraGraph, overallMaxPeriod]);

  const currentTimestamp = useMemo(() => {
    if (isBuoySwellSpectraGraph) {
      return currentBuoyTimestamp || 0;
    }
    return days?.[dayIndex]?.[hourIndex]?.timestamp || 0;
  }, [currentBuoyTimestamp, dayIndex, days, hourIndex, isBuoySwellSpectraGraph]);

  const formattedBuoySpectraReadingsData = useMemo(() => {
    if (!isBuoySwellSpectraGraph) return null;

    const data = activeWave?.data?.swellSpectra?.spectraFormattedData;
    if (interval === 1) {
      return data;
    }

    // Note: Data is not ready and comes in as undefined on first render navigating from spot page to buoy page
    return data?.filter((p: { period: number }) => p.period % interval === 0);
  }, [activeWave?.data, interval, isBuoySwellSpectraGraph]);

  const formattedBuoySpectraData = useMemo(
    () => activeWave?.data?.swellSpectra?.spectraFormattedData ?? null,
    [activeWave?.data],
  );

  const utcOffset = useMemo(
    () => swellSpectraData?.days?.[daysToDisplay[dayIndex]]?.[hourIndex]?.utcOffset || 0,
    [dayIndex, daysToDisplay, hourIndex, swellSpectraData?.days],
  );

  const showSwellSpectraGraphContents = useMemo(
    () =>
      isBuoySwellSpectraGraph
        ? !!formattedBuoySpectraData?.length
        : !!(
            periodRange?.length &&
            currentSpread?.length &&
            currentDirection?.length &&
            currentEnergy?.length &&
            currentTimestamp
          ),
    [
      currentDirection?.length,
      currentEnergy?.length,
      currentSpread?.length,
      currentTimestamp,
      formattedBuoySpectraData,
      isBuoySwellSpectraGraph,
      periodRange?.length,
    ],
  );

  /**
   * The maxSwellEnergy will essentially be the single source of truth controlling wether the user experiences
   * a dynamic scale or a fixed scale on the swell spectra graph
   */
  const maxSwellEnergy = useMemo(() => {
    if (fixedScale) {
      return Math.max(
        ...(hours?.map((spectra: SwellSpectraWaveDataPoint) =>
          Math.max(...(spectra?.energy || [0])),
        ) || []),
      );
    }

    if (isBuoySwellSpectraGraph) {
      const hourlyMaxSwellEnergy = activeWave?.data?.swellSpectra?.energy || [0];
      return Math.max(...hourlyMaxSwellEnergy);
    }

    const hourlyMaxSwellEnergy = days?.[dayIndex]?.[hourIndex]?.energy || [0];
    return Math.max(...hourlyMaxSwellEnergy);
  }, [fixedScale, isBuoySwellSpectraGraph, days, dayIndex, hourIndex, hours, activeWave?.data]);

  const graphPaddingX = isDesktop
    ? SWELL_SPECTRA_GRAPH_X_PADDING
    : SWELL_SPECTRA_GRAPH_X_PADDING_MOBILE;

  const xScale = scaleLinear()
    .range([graphPaddingX, graphWidth - graphPaddingX])
    .domain([periodRange[0], periodRange[periodRange.length - 1]]);

  const yScale = scaleLinear()
    .range([
      SWELL_SPECTRA_GRAPH_HEIGHT - SWELL_SPECTRA_GRAPH_Y_PADDING,
      SWELL_SPECTRA_GRAPH_Y_PADDING,
    ])
    .domain([0, maxSwellEnergy]);

  const swellTooltipContainerStyles = useCallback(
    (currentPeriod: number, previousPeriod?: number) => {
      const tooltipPadding = 6;
      const tooltipHeight = 64;
      const totalTooltipHeight = tooltipHeight + tooltipPadding;

      let peakPosition = yScale(
        isBuoySwellSpectraGraph
          ? formattedBuoySpectraData?.[currentPeriod - 2]?.energy || totalTooltipHeight
          : currentRawEnergy?.[currentPeriod - 2] || totalTooltipHeight,
      );
      let showTooltipBelow = peakPosition > totalTooltipHeight;

      if (currentPeriod === previousPeriod) {
        showTooltipBelow = peakPosition > totalTooltipHeight * 2;
        if (showTooltipBelow) {
          peakPosition -= tooltipHeight;
        } else if (!showTooltipBelow && peakPosition > totalTooltipHeight) {
          peakPosition += 0;
        } else if (!showTooltipBelow) {
          peakPosition += tooltipHeight;
        }
      }

      const tooltipTop = showTooltipBelow
        ? peakPosition - totalTooltipHeight
        : peakPosition + tooltipPadding;

      return {
        display: tooltipTop < 0 ? 'none' : 'block',
        left: xScale(currentPeriod) - 46,
        top: tooltipTop,
      };
    },
    [currentRawEnergy, isBuoySwellSpectraGraph, formattedBuoySpectraData, xScale, yScale],
  );

  return {
    currentDirection,
    currentEnergy,
    currentRawEnergy,
    currentSpread,
    currentTimestamp,
    utcOffset,
    periodRange,
    days,
    hours,
    maxSwellEnergy,
    rawPeriodRange,
    swellTooltipContainerStyles,
    showSwellSpectraGraphContents,
    formattedBuoySpectraReadingsData,
    formattedBuoySpectraData,
    rawBuoyPeriodRange,
    sortedTop3Swells,
    xScale,
    yScale,
  };
};

export default useSwellSpectra;
